<template>
	<v-container style="background-color: #ffffff">
		<p style="margin: 30px 50px;">
			NIS stands for Network and Information Systems.
			Explore your company's cybersecurity performance in detail under this tab.
			This score acts as a standard for measuring progress and ensuring adherence
			to responsible security practices, aligning with the requirements outlined
			in the EU NIS2 Directive (the Network and Information Systems Directive)
			for a more resilient digital Europe.
		</p>

		<template v-if="hasScore">
			<v-row class="contentRow">
				<v-col cols="12">
					<nis-score-rings
						:score1="score1"
						:score2="score2"
						:score3="score3"
						:score4="score4"
						:score5="score5"
						:score6="score6"
						title="Your NIS Cyber Security Score"
					/>
				</v-col>
				<v-col cols="12" class="text-center">
					<p v-if="scoreData.length > 1" style="font-size: 20px;">
						Your Average NIS Score for
						<strong>{{ year1 }} : {{ avgScore1 }}%</strong>
						and for
						<strong>{{ year2 }} : {{ avgScore2 }}%</strong>.
					</p>
					<p v-else style="font-size: 20px;">
						Your Average NIS Score for
						<strong>{{ year1 }} : {{ avgScore1 }}%</strong>
					</p>
					<p style="font-size: 20px; font-weight: bold;">
						Filled out : {{ filledOutDate }}
					</p>
				</v-col>
			</v-row>

			<br />
			<br />
			<br />

			<v-col cols="12">
				<h3 class="timelinetitle">
					NIS score of two consecutive years ( 2 - Year Comparison)
				</h3>
				<br />
				<nis-score-timeline :year-count="2" />
			</v-col>

			<br />
			<div class="flex-container">
				<h3>Click here for more comparisons</h3>
				<v-btn class="mr-10" color="primary" @click="show = !show">
					Detailed View
				</v-btn>
			</div>
			<br />
			<v-col cols="12" v-if="show">
				<h3 class="timelinetitle">
					Your NIS Score Over the Years - (5 - Year Comparison)
				</h3>
				<br />
				<nis-score-timeline :year-count="5" />
			</v-col>
		</template>

		<template v-else-if="!loading">
			<v-row class="contentRow" justify="center">
				<v-col cols="12" md="8" class="text-center py-8">
					<p>
						You have not completed the NIS Cyber Security Self-Assessment yet.
					</p>
					<v-btn color="primary" to="/nis" class="mt-4">
						Go to Survey
					</v-btn>
				</v-col>
			</v-row>
		</template>
	</v-container>
</template>

<script>
import NisApi from "../api/nis/nis"
import LoadingState from "../consts/LoadingState"
import NisScoreRings from "../components/NisScoreRings.vue"
import NisScoreTimeline from "../components/NisScoreTimeline.vue"

export default {
	name: "NisYourScore",
	components: {
		"nis-score-rings": NisScoreRings,
		"nis-score-timeline": NisScoreTimeline,
	},
	data() {
		return {
			loading: true,
			hasScore: false,
			show: false,
			scoreData: [],
			score1: null,
			score2: null,
			score3: null,
			score4: null,
			score5: null,
			score6: null,
			avgScore1: 0,
			avgScore2: 0,
			year1: 0,
			year2: 0,
			filledOutDate: "",
		}
	},
	mounted() {
		this.loadScores()
	},
	methods: {
		averageOf(entry) {
			const scores = [
				entry.score1,
				entry.score2,
				entry.score3,
				entry.score4,
				entry.score5,
				entry.score6,
			].filter((s) => s != null && !isNaN(s))
			if (!scores.length) return 0
			return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
		},
		loadScores() {
			this.loading = true
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				null,
				{ root: true }
			)
			NisApi.getNisAssessmentsAll()
				.then((result) => {
					const list = Array.isArray(result?.[0]) ? result[0] : []
					this.scoreData = list.map((item) => {
						const date = new Date(item.timestamp)
						return {
							year: date.getFullYear(),
							timestamp: item.timestamp,
							score1: item.score1 == null ? null : Math.round(item.score1),
							score2: item.score2 == null ? null : Math.round(item.score2),
							score3: item.score3 == null ? null : Math.round(item.score3),
							score4: item.score4 == null ? null : Math.round(item.score4),
							score5: item.score5 == null ? null : Math.round(item.score5),
							score6: item.score6 == null ? null : Math.round(item.score6),
						}
					})

					if (this.scoreData.length > 0) {
						const latest = this.scoreData[0]
						this.hasScore = true
						this.score1 = latest.score1
						this.score2 = latest.score2
						this.score3 = latest.score3
						this.score4 = latest.score4
						this.score5 = latest.score5
						this.score6 = latest.score6
						this.avgScore1 = this.averageOf(latest)
						this.year1 = latest.year
						if (latest.timestamp) {
							const date = new Date(latest.timestamp)
							const month = date.toLocaleString("default", { month: "short" })
							this.filledOutDate = `${month} ${date.getFullYear()}`
						}
						if (this.scoreData.length > 1) {
							this.avgScore2 = this.averageOf(this.scoreData[1])
							this.year2 = this.scoreData[1].year
						}
					} else {
						this.hasScore = false
					}

					this.loading = false
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_END_LOADING,
						null,
						{ root: true }
					)
				})
				.catch((error) => {
					this.loading = false
					this.hasScore = false
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
		},
	},
}
</script>

<style scoped>
.flex-container {
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 5%;
}

.timelinetitle {
	display: flex;
	justify-content: center;
}
</style>
